import { useMemo, useState, type JSX } from "react";
import {
  useAddOrganizationMember,
  useGetOrganizationMembers,
  useRemoveOrganizationMember,
  useUpdateOrganizationMemberRole,
} from "@via/pages/account/admin/organizations/organizations.api.tsx";
import { SkeletonLoader, Spinner } from "@via/components/loading/loading";
import { GetUserOrganizationDetailsForAdminResponseBodyItem } from "common";
import { EmptyDataIcon } from "@via/utils/custom-icons.tsx";

/**
 * The Members tab: add, remove, and re-role the members of any organization, including one the
 * administrator does not belong to.
 *
 * That last part is the reason this exists rather than reusing the organization's own Users tab:
 * that tab renders the signed-in user's first organization regardless of the id in the URL, so it
 * cannot reach an organization nobody has been attached to yet.
 */
export default function MembersPanel({
  organization,
}: {
  organization: GetUserOrganizationDetailsForAdminResponseBodyItem;
}): JSX.Element {
  const organizationId = organization.id;

  // The panel only mounts while its tab is active, so the query no longer needs an open flag to
  // gate it. It kept one when this was a dialog that stayed mounted behind a closed backdrop.
  const { data, isLoading, isError } = useGetOrganizationMembers(
    { organizationId },
    !!organizationId
  );

  const members = data?.members ?? [];
  const roles = data?.roles ?? [];

  const { mutateAsync: addMember, isPending: isAdding } =
    useAddOrganizationMember();
  const { mutateAsync: updateRole, isPending: isUpdatingRole } =
    useUpdateOrganizationMemberRole();
  const { mutateAsync: removeMember, isPending: isRemoving } =
    useRemoveOrganizationMember();

  const [email, setEmail] = useState("");
  const [newMemberRoleId, setNewMemberRoleId] = useState("");
  const [busyUserId, setBusyUserId] = useState<number | null>(null);

  // Roles arrive with the member list because their ids are scoped to this one organization. Until
  // they land there is no valid role to offer, so the add control stays disabled.
  const defaultRoleId = useMemo(() => {
    if (newMemberRoleId) return newMemberRoleId;
    const member = roles.find(role => role.name === "member");
    return member?.id ?? roles[0]?.id ?? "";
  }, [newMemberRoleId, roles]);

  const canAdd =
    !!organizationId && email.trim().length > 0 && !!defaultRoleId && !isAdding;

  const onAdd = async () => {
    if (!canAdd) return;
    await addMember({
      organizationId,
      email: email.trim(),
      roleId: defaultRoleId,
    });
    setEmail("");
  };

  const onChangeRole = async (userId: number, roleId: string) => {
    setBusyUserId(userId);
    try {
      await updateRole({ organizationId, userId, roleId });
    } finally {
      setBusyUserId(null);
    }
  };

  const onRemove = async (userId: number) => {
    setBusyUserId(userId);
    try {
      await removeMember({ organizationId, userId });
    } finally {
      setBusyUserId(null);
    }
  };

  const displayName = (member: (typeof members)[number]): string => {
    const full = [member.firstName, member.lastName]
      .filter(part => !!part && part.trim().length > 0)
      .join(" ");
    return full || member.username;
  };

  return (
    <div data-testid="members-panel-list" className="space-y-6">
      <div className="text-sm text-gray-700">
        Add an existing account to this organization, change what a member can
        do, or remove them. The account has to exist already: adding a member
        here does not create a person. Removing somebody leaves their account
        active, so use the Users tab if you also want to deactivate it.
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-900">
            Email of an existing account
          </label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="person@example.edu"
            className="mt-2 block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-900">
            Role
          </label>
          <select
            value={defaultRoleId}
            onChange={e => setNewMemberRoleId(e.target.value)}
            disabled={roles.length === 0}
            className="mt-2 block rounded-md border-0 py-2 pr-8 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 disabled:opacity-50 sm:text-sm">
            {roles.map(role => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => void onAdd()}
          disabled={!canAdd}
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50">
          <Spinner loading={isAdding}>Add</Spinner>
        </button>
      </div>

      <div className="rounded-md border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
          Members ({members.length})
        </div>
        {isLoading ? (
          <div className="p-4">
            <SkeletonLoader count={1} />
          </div>
        ) : isError ? (
          <div className="p-6 text-sm text-red-600">
            This organization&apos;s members could not be loaded.
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-6 text-sm text-gray-600">
            <EmptyDataIcon />
            <div>Nobody belongs to this organization yet.</div>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {members.map(member => (
              <li
                key={member.userOrganizationId}
                className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-gray-900">
                    {displayName(member)}
                  </div>
                  <div className="truncate text-gray-500">
                    {member.email ?? member.username}
                  </div>
                </div>
                <select
                  value={
                    roles.find(
                      role => role.name === member.userOrganizationRole
                    )?.id ?? ""
                  }
                  onChange={e =>
                    void onChangeRole(member.userId, e.target.value)
                  }
                  disabled={
                    roles.length === 0 ||
                    (isUpdatingRole && busyUserId === member.userId)
                  }
                  className="rounded-md border-0 py-2 pr-8 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 disabled:opacity-50">
                  {/*
                      An empty option appears only when the membership names a role this
                      organization does not define, which is a data fault worth showing rather
                      than silently rendering as the first role in the list.
                    */}
                  {!roles.some(
                    role => role.name === member.userOrganizationRole
                  ) && <option value="">{member.userOrganizationRole}</option>}
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => void onRemove(member.userId)}
                  disabled={isRemoving && busyUserId === member.userId}
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
                  <Spinner loading={isRemoving && busyUserId === member.userId}>
                    Remove
                  </Spinner>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
